import {sum, uniq} from 'lodash';
import {useMemo} from 'react';
import {createSelector} from 'reselect';

import {useDeepMemo, useMemoizedSelector} from '../../../state/hooks';
import {type CustomRunNames} from '../../../state/views/customRunNames/types';
import {type Ref as RunSetRef} from '../../../state/views/runSet/types';
import * as Filter from '../../../util/filters';
import type * as FilterTypes from '../../../util/filterTypes';
import {addSearchFilter, sortToOrderString} from '../../../util/queryts';
import type * as QueryTypes from '../../../util/queryTypes';
import type * as RunFeed from '../../../util/runfeed';
import * as Run from '../../../util/runs';
import type * as RunTypes from '../../../util/runTypes';
import * as SelectionManager from '../../../util/selectionmanager';
import {RUN_TAGS_COL} from '../../../util/tablecols';
import {makeRunSetSelector} from '../runSetSelectors';
import {type RunSelectorRunsPartialVars} from '../types';

type TopLevelQueryVariablesArgs = {
  customRunNames?: CustomRunNames;
  enableArtifactCounts: boolean;
  entityName: string;
  groupSelections: SelectionManager.GroupSelectionState;
  isTableCollapsed: boolean;
  mergeFilters?: FilterTypes.Filter<RunTypes.Key>;
  projectInternalId?: string;
  projectName: string;
  runSetRef: RunSetRef;
  runSetFilters: FilterTypes.RootFilter<RunTypes.Key>;
  sort: QueryTypes.Sort;
  tableSettings: RunFeed.Config;
};

const makeTopLevelQueryVariablesSelector = (runSetRef: RunSetRef) =>
  createSelector(makeRunSetSelector(runSetRef), runSet => ({
    baselineRunId: runSet.baselineRunId,
    pinnedRunIds: runSet.pinnedRunIds ?? [],
    search: runSet.search,
  }));

export const useTopLevelQueryVariables = ({
  customRunNames,
  enableArtifactCounts,
  entityName,
  groupSelections,
  isTableCollapsed,
  mergeFilters,
  projectInternalId,
  projectName,
  runSetRef,
  runSetFilters,
  sort,
  tableSettings,
}: TopLevelQueryVariablesArgs): RunSelectorRunsPartialVars => {
  const partialRunSet = useMemoizedSelector(
    makeTopLevelQueryVariablesSelector,
    runSetRef
  );

  const {grouping} = groupSelections;
  const {columnOrder, columnPinned} = tableSettings;

  const filters = useMemo(() => {
    const mergedFilters =
      mergeFilters != null
        ? Filter.mergedFilters(runSetFilters, mergeFilters)
        : Filter.mergedFilters(runSetFilters);
    const filtersWithSearch = addSearchFilter(
      mergedFilters,
      partialRunSet.search,
      grouping,
      customRunNames
    );
    return filtersWithSearch;
  }, [
    customRunNames,
    grouping,
    mergeFilters,
    partialRunSet.search,
    runSetFilters,
  ]);

  const selections = useMemo(
    () => SelectionManager.toRunFilter(groupSelections),
    [groupSelections]
  );

  let queryColumnOrder = columnOrder;
  if (isTableCollapsed) {
    // If the table is collapsed, only fetch the pinned columns.
    const numPinnedColumns = sum(Object.values(columnPinned));
    queryColumnOrder = columnOrder.slice(0, numPinnedColumns);
  }

  const fetchColumns: RunTypes.Key[] = useMemo(
    () => [
      ...grouping,
      ...(queryColumnOrder
        .map(Run.keyFromString)
        .filter(key => !!key) as RunTypes.Key[]),
      ...(selections != null
        ? Filter.getUniqueKeysUsedInFilter(selections)
        : []),
    ],
    [grouping, queryColumnOrder, selections]
  );

  const topLevelQueryVariables = useDeepMemo(
    useMemo(() => {
      const configKeys = fetchColumns
        .filter(key => key.section === 'config')
        .map(key => key.name);
      const summaryKeys = fetchColumns
        .filter(key => key.section === 'summary')
        .map(key => key.name);
      const aggregationKeys = uniq(
        fetchColumns
          .filter(
            key =>
              key.section === 'aggregations_min' ||
              key.section === 'aggregations_max'
          )
          .map(key => key.name)
      );
      const enableTags = fetchColumns.some(
        key => Run.keyToString(key) === RUN_TAGS_COL
      );

      return {
        aggregationKeys,
        baselineRunId: grouping.length
          ? undefined
          : partialRunSet.baselineRunId,
        configKeys,
        enableAggregations: aggregationKeys.length > 0,
        enableArtifactCounts,
        enableConfig: configKeys.length > 0,
        enableSummary: summaryKeys.length > 0,
        enableTags,
        entityName,
        filters,
        grouping,
        groupLevel: 0,
        order: sortToOrderString(sort),
        projectInternalId,
        projectName,
        pinnedRunIds: grouping.length ? [] : partialRunSet.pinnedRunIds,
        selections,
        summaryKeys,
        wandbKeys: ['cli_version', 'is_jupyter_run'],
      };
    }, [
      enableArtifactCounts,
      entityName,
      fetchColumns,
      filters,
      grouping,
      projectInternalId,
      projectName,
      partialRunSet.baselineRunId,
      partialRunSet.pinnedRunIds,
      selections,
      sort,
    ])
  );

  return topLevelQueryVariables;
};
